import { Component, Input, OnInit, OnChanges, Output, EventEmitter, SimpleChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup, FormArray, FormBuilder, Validators } from '@angular/forms';
import { MasterService } from '../../../../../../services/master.service'
import { MessageService } from '../../../../../../services/message.service'
import { StoreService } from '../../../../../../services/store.service'
import * as languageLibrary from '../../../../../../services/language'
import { Subscription } from 'rxjs';
import { SAVE_DEBOUNCE_MILLIS, TAX_TYPES, SALE_TYPES, ITEMIZED_FEE_CATEGORIES } from '../../../../../../constants/sale'
import { formatCurrency, formatPercent } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import { TaxType } from 'src/app/models/tax-type.model';

@Component({
    selector: 'ttl',
    templateUrl: './ttl.component.html',
    styleUrls: ['./ttl.component.scss'],
    standalone: false
})
export class TtlComponent implements OnInit, OnChanges, OnDestroy {
    //
    @Input() saleId = null

    //
    @Input() saleType: number | null = null

    //
    @Input() isUnLock = true

    //
    protected baseSalePrice: number = 0

    // Total cost of any sale accessories.
    protected accessories: number = 0

    //
    protected totalSalePrice: number = 0

    //
    protected driveOutPrice: number = 0

    //
    protected inventoryTaxRate: number = 0

    //
    protected taxTypes: TaxType[] = TAX_TYPES

    //  An associative array of sale types with the id as the key
    protected saleTypes: Record<number, string> = SALE_TYPES

    protected feesId: number = 0

    // define loading status
    protected loading: boolean = false

    // save the language
    protected language: string = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    /** A setTimeout watcher to delay computations during times of rapid changes (first load) */
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for component events and make decisions. */
    private componentWatcher: Subscription | null = null

    private subscriptions: Subscription[] = []

    public form: FormGroup | null = null

    /** Computed total of all fees. */
    protected total: number = 0

    get feeable(): boolean { return ((this.saleType) && (['Cash deal', 'Outside', 'BHPH'].includes(this.saleTypes[this.saleType]))) }
    get outside(): boolean { return ((this.saleType) && (this.saleTypes[this.saleType] === 'Outside')) }
    get bhph(): boolean { return ((this.saleType) && (this.saleTypes[this.saleType] === 'BHPH')) }
    get outsideOrCash(): boolean { return ((this.saleType) && (['Outside', 'Cash deal'].includes(this.saleTypes[this.saleType]))) }
    get outOfState(): boolean { return this.form.get('outOfState').value }

    /**
     * Helper method to consistently get the list of form fees as an Array.
     */
    get fees() {
        return this.form.controls['fees'] as FormArray;
    }

    constructor(private master: MasterService, private ms: MessageService, private store: StoreService, private fb: FormBuilder) {
        this.form = this.fb.group({
            outOfState: this.fb.control([false, []]),
            inventoryTaxType: this.fb.control([null, []]),
            fees: this.fb.array([])
        })
    }

    ngOnInit() {
        this.read()

        this.subscriptions.push(
            this.form.get('outOfState').valueChanges.subscribe((newValue) => {
                if (!this.fees || (this.fees.length <= 0)) {
                    return
                }

                this.rebuild()
                this.calculate(true)
            })
        )

        this.subscriptions.push(
            this.form.get('inventoryTaxType').valueChanges.subscribe((newValue) => {
                if (!this.fees || (this.fees.length <= 0)) {
                    return
                }

                this.updateInventoryTaxRate()
                this.calculate(true)
            })
        )

        this.componentWatcher = this.ms.channelComponents$.subscribe((event) => {
            if (!event || !event.message) {
                return
            }

            switch (event.message) {
                case 'ttlReset':
                case 'saleTypeChanged':
                case 'saleCreated':
                case 'buyerAdded':
                case 'vehicleAdded':
                    this.generate()
                    break
                case 'saleRead':
                    this.baseSalePrice = event.value.baseSalePrice
                    this.totalSalePrice = event.value.totalSalePrice
                    this.driveOutPrice = event.value.driveOutPrice
                    this.calculate(false, true)
                    break
                case 'salePriceChanged':
                case 'totalSalePriceChanged':
                    this.baseSalePrice = event.value.baseSalePrice
                    this.totalSalePrice = event.value.totalSalePrice
                    this.driveOutPrice = event.value.driveOutPrice
                    this.calculate(true)
                    break
                case 'accessoriesRead':
                case 'accessoriesChanged':
                    this.accessories = event.value.value
                    break
            }
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes) {
            return
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.isUnLock) {
            this.form.disable({ emitEvent: false })
            if (this.isUnLock) {
                this.form.enable({ emitEvent: false })
            }
        }

        if (changes.saleId) {
            this.read()
        }
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }
        if (this.componentWatcher) {
            this.componentWatcher.unsubscribe()
        }
        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    /*
        reload and clean the inputs
    */
    private reset = (): void => {
        this.form.reset(undefined, { emitEvent: false })
        this.fees.clear({ emitEvent: false })
    }

    private updateInventoryTaxRate = () => {
        let inventoryTaxRateFee = this.fees.controls.find(fee => fee.value['category'] == "Inventory Tax")
        let inventoryTaxRateFeeIndex = this.fees.controls.findIndex(fee => fee.value['category'] == "Inventory Tax")
        switch (this.form.get("inventoryTaxType").value) {
            case "MV":
                this.restore(inventoryTaxRateFeeIndex)
                break;
            case "SS":
            case "FL":
            case "DL":
                inventoryTaxRateFee.get('custom').setValue(formatPercent(0), { emitEvent: true })
                break;
            default:
                break;
        }
    }

    // =============================
    // read sale taxes and fees
    // =============================
    private read = (): void => {
        if (this.loading) {
            return
        }

        const identity = parseInt(this.saleId)
        if (isNaN(identity) || identity <= 0) {
            return
        }

        this.reset()
        this.loading = true
        this.master.get(`sales/${identity}/fees`, res => {
            this.loading = false

            if (!res || !res.data) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (!res.data.saleTaxAndFees) {
                this.generate()
                return
            }

            this.form.get('outOfState').setValue(res.data.saleTaxAndFees.outOfState, { emitEvent: false })
            this.form.get('inventoryTaxType').setValue(res.data.saleTaxAndFees.inventoryTaxType !== null ? res.data.saleTaxAndFees.inventoryTaxType : "MV", { emitEvent: false })
            this.assemble(res.data, false)
            this.calculate(false, true)
        })
    }

    // =============================
    // Create sale taxes and fees
    // =============================
    private generate = (): void => {
        if (this.loading) {
            return
        }

        if (!this.saleId || (this.saleId <= 0)) {
            return
        }

        this.reset()
        this.loading = true
        this.master.post(`sales/${this.saleId}/fees`, { inventoryTaxType: this.form.get("inventoryTaxType").value }, res => {
            this.loading = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            if (res.data.saleTaxAndFees) {
                this.form.patchValue({
                    outOfState: res.data.saleTaxAndFees.outOfState,
                    inventoryTaxType: res.data.saleTaxAndFees.inventoryTaxType
                }, { emitEvent: false })
            }

            this.assemble(res.data, true)
            this.calculate(true)
        })
    }

    private assemble = (meta, save: boolean = false) => {
        if (!meta || !meta.saleTaxAndFees || !meta.saleTaxAndFees.data) {
            return
        }

        const saleTaxAndFees = meta.saleTaxAndFees.data
        this.feesId = parseInt(meta.saleTaxAndFees.id)

        const fees = this.fees

        saleTaxAndFees['taxes'].forEach(el => {
            el['amount'] = ((+el['rate'] * (+this.totalSalePrice)) / 100)
            if (this.totalSalePrice <= 0) {
                el['amount'] = 0
            }

            el['newRate'] = ((el['newRate'] !== undefined) && (el['newRate'] !== null)) ? (+el['newRate']).toFixed(4) : el['rate']

            if (el['category'] == 'Inventory Tax') {
                el['amount'] = ((+el['rate'] * (this.baseSalePrice + this.accessories)) / 100)
                this.inventoryTaxRate = clean((el['rate'] || 0))
                if (this.form.get("inventoryTaxType").value == "SS") {
                    el['newRate'] = 0
                }
            }

            fees.push(this.fb.group({
                feeId: [el['feeId'], []],
                titleId: [el['titleId'], []],
                name: [el['name'], []],
                amount: [formatCurrency(el['amount']), []],
                category: [el['category'], []],
                rate: [formatPercent(el['rate']), []],
                custom: [formatPercent(el['newRate']), []],
                formula: [el['feeFormula'] || el['taxFormula'], []],
                source: [el['sourceType'], []],
                tax: [true, []],
                sticky: el['sticky']
            }, { emitEvent: save }))

            const fee = fees.at(this.fees.length - 1)
            if (fee.get('rate').value != fee.get('custom').value) {
                fee.get('custom').markAsDirty()
            }
        })

        // initialize new fields of fees
        saleTaxAndFees['fees'].forEach(el => {
            el['amount'] = parseFloat(el['amount']).toFixed(2)
            el['newAmount'] = ((el['newAmount'] !== undefined) && (el['newAmount'] !== null)) ? el['newAmount'] : el['amount']

            fees.push(this.fb.group({
                feeId: [el['feeId'], []],
                titleId: [el['titleId'], []],
                name: [el['name'], []],
                amount: [formatCurrency(el['amount']), []],
                custom: [formatCurrency(el['newAmount']), []],
                category: [el['category'], []],
                rate: [formatPercent(el['rate']), []],
                formula: [el['feeFormula'], []],
                source: [el['sourceType'], []],
                tax: [false, []],
                sticky: el['sticky']
            }, { emitEvent: save }))

            const fee = fees.at(this.fees.length - 1)
            if (fee.get('amount').value != fee.get('custom').value) {
                fee.get('custom').markAsDirty()
            }
        })

        this.form.disable({ emitEvent: false })
        if (this.isUnLock) {
            this.form.enable({ emitEvent: false })
        }
    }

    /**
     * When outOfState changes we need to zero out or 
     * put back the old fees based on its current value.
     */
    private rebuild = () => {
        if (!this.fees || (this.fees.length <= 0) || !this.feesId || (this.feesId <= 0)) {
            return
        }

        for (let fee of this.fees.controls) {
            if (fee.get('sticky').value == true) {
                continue
            }

            const isRate: boolean = (fee.get('formula').value && (fee.get('formula').value.length > 0))

            if (this.outOfState) {
                if (isRate) {
                    fee.get('custom').setValue(formatPercent(0))
                    continue
                }

                fee.get('custom').setValue(formatCurrency(0))
                continue
            }

            if (isRate) {
                fee.get('custom').setValue(fee.get('rate').value)
                continue
            }

            fee.get('custom').setValue(fee.get('amount').value)
        }
    }

    // todo: recalculate
    private calculate = (save: boolean = false, init: boolean = false) => {
        if (!this.fees || !this.feesId || (this.feesId <= 0)) {
            return
        }

        let total: number = 0
        let salesTax: number = 0
        let useTax: number = 0
        let salesTaxRate: number = 0
        let useTaxRate: number = 0
        let itemized: number = 0

        const fees = []

        for (let fee of this.fees.controls) {
            const isTax: boolean = (fee.get('tax').value == true)
            const name: string = fee.get('name').value
            const category = fee.get('category').value.toLowerCase()

            if (isTax) {
                const rate = clean(fee.get('custom').value)

                fee.get('amount').setValue('0.00')
                if (this.totalSalePrice > 0) {
                    fee.get('amount').setValue(formatCurrency(this.totalSalePrice * (rate / 100)))
                }

                switch (category) {
                    case 'sales tax':
                        salesTax = clean(fee.get('amount').value)
                        salesTaxRate = rate
                        break
                    case 'inventory tax':
                        fee.get('amount').setValue(formatCurrency((this.baseSalePrice + this.accessories) * (rate / 100)))
                        break
                    case 'use tax':
                        useTax = clean(fee.get('amount').value)
                        useTaxRate = rate
                        break
                }

                total += clean(fee.get('amount').value)

                continue
            }

            const formula = fee.get('formula').value
            if (formula && (formula.length > 0)) { // we have a calculated fee
                const rate = clean(fee.get('custom').value)
                fee.get('amount').setValue(formatCurrency(this.baseSalePrice * (rate / 100)))

                fees.push({
                    feeId: fee.get('feeId').value,
                    titleId: fee.get('titleId').value,
                    name: name,
                    amount: clean(fee.get('amount').value),
                    newAmount: clean(fee.get('amount').value),
                    category: fee.get('category').value,
                    rate: rate,
                    newRate: clean(fee.get('custom').value),
                    feeFormula: fee.get('formula').value,
                    sourceType: fee.get('source').value,
                    sticky: fee.get('sticky').value
                }, { emitEvent: save })

                total += clean(fee.get('amount').value)

                continue
            }

            total += clean(fee.get('custom').value)

            fees.push({
                feeId: fee.get('feeId').value,
                titleId: fee.get('titleId').value,
                name: name,
                amount: clean(fee.get('amount').value),
                newAmount: clean(fee.get('custom').value),
                category: fee.get('category').value,
                rate: clean(fee.get('rate').value),
                feeFormula: fee.get('formula').value,
                sourceType: fee.get('source').value,
                sticky: fee.get('sticky').value
            }, { emitEvent: save })

            if (ITEMIZED_FEE_CATEGORIES.includes(fee.get('category').value.toLowerCase())) {
                itemized += parseFloat(fee.get('amount').value)
            }
        }

        const payload = { value: total, salesTax: salesTax, salesTaxRate: salesTaxRate, fees: fees, save: save, inventoryTaxRate: this.inventoryTaxRate, itemized: itemized }
        this.total = total

        if (init) {
            this.ms.channelComponents$.emit({ message: 'ttlRead', section: 'ttl', value: payload })
        }

        if (!save) {
            return
        }

        this.save(payload)
    }

    // =============================
    // Update taxes and fees record in storage.
    // =============================
    private save = (changes: any): void => {
        if (!this.fees || !this.feesId || (this.feesId <= 0)) {
            return
        }

        const payload = {
            outOfState: this.form.get('outOfState').value,
            inventoryTaxType: this.form.get('inventoryTaxType').value,
            itemized: 0,
            meta: { taxes: [], fees: [] }
        }

        for (let fee of this.fees.controls) {
            const isTax: boolean = (fee.get('tax').value == true)
            const isCalculated: boolean = ((fee.get('formula').value) && (fee.get('formula').value.length > 0))

            if (isTax) {
                payload.meta.taxes.push({
                    feeId: fee.get('feeId').value,
                    titleId: fee.get('titleId').value,
                    name: fee.get('name').value,
                    amount: clean(fee.get('amount').value),
                    newAmount: clean(fee.get('amount').value),
                    category: fee.get('category').value,
                    rate: clean(fee.get('rate').value),
                    newRate: clean(fee.get('custom').value),
                    taxFormula: fee.get('formula').value,
                    sourceType: fee.get('source').value,
                    sticky: fee.get('sticky').value
                })

                continue
            }

            if (isCalculated) { // Inventory Tax and similar
                payload.meta.fees.push({
                    feeId: fee.get('feeId').value,
                    titleId: fee.get('titleId').value,
                    name: fee.get('name').value,
                    amount: clean(fee.get('amount').value),
                    newAmount: clean(fee.get('amount').value),
                    category: fee.get('category').value,
                    rate: clean(fee.get('rate').value),
                    newRate: clean(fee.get('custom').value),
                    feeFormula: fee.get('formula').value,
                    sourceType: fee.get('source').value,
                    sticky: fee.get('sticky').value
                })

                continue
            }

            payload.meta.fees.push({
                feeId: fee.get('feeId').value,
                titleId: fee.get('titleId').value,
                name: fee.get('name').value,
                amount: clean(fee.get('amount').value),
                newAmount: clean(fee.get('custom').value),
                category: fee.get('category').value,
                rate: clean(fee.get('rate').value),
                feeFormula: fee.get('formula').value,
                sourceType: fee.get('source').value,
                sticky: fee.get('sticky').value
            })

            if (ITEMIZED_FEE_CATEGORIES.includes(fee.get('category').value.toLowerCase())) {
                payload.itemized += parseFloat(fee.get('amount').value)
            }
        }

        this.master.put(`sales/${this.saleId}/fees/${this.feesId}`, payload, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
            }

            this.ms.channelComponents$.emit({ message: 'ttlChanged', section: 'ttl', value: changes })
        })
    }

    /**
     * 
     * @param e 
     */
    protected changed = (e: Event): void => {
        this.calculate(true)
    }

    /**
     * Restore the default value for a fee.
     * @param index The index of the fee to restore
     */
    protected restore = (index: number): void => {
        if ((index < 0) || (index > this.fees.length - 1)) {
            return
        }

        const formula = this.fees.at(index).get('formula').value
        const rateBased = (formula && (formula.length > 0))

        if (rateBased) {
            this.fees.at(index).get('custom').setValue(this.fees.at(index).get('rate').value)
            this.fees.at(index).get('custom').markAsPristine()
            this.calculate(true)
            return
        }

        this.fees.at(index).get('custom').setValue(this.fees.at(index).get('amount').value)
        this.fees.at(index).get('custom').markAsPristine()
        this.calculate(true)
    }

    /**
     * Remove a communication method from the form array
     * @param index The index of the fee to discard
     */
    protected discard = (index: number): void => {
        if ((index < 0) || (index > this.fees.length - 1)) {
            return
        }

        this.fees.removeAt(index)
    }

}
